// Sample code present in
// https://github.com/immersive-web/webxr-samples/blob/main/hit-test.html
import * as THREE from "three";
import Stats from "three/examples/jsm/libs/stats.module";
import {
  AR_EVENTS,
  AR_PLATFORM,
  AR_POSITION_SEEK,
  AR_SUPPORT,
  THREEJS_NAMES,
} from "../constants";
import Event from "./Event";
import { createWebGLContext } from "./ThreejsUtility";

const mod = () => {
  let support = AR_SUPPORT.INITIALIZING;
  let reason = "INITIALIZING";
  let session = null;
  let userActivationReqd = false;
  let gl = null;
  let stats = null;
  let scene = null;
  let camera = null;
  let renderer = null;
  let referenceSpace = null;
  let hitTestSource = null;
  let hitTestStatus = false;
  let seekStartEvent = false;
  const hitMatrix = new THREE.Matrix4();
  let hitTestCallback = () => {};
  const hitTestPosition = new THREE.Vector3();
  const hitTestRotation = new THREE.Quaternion();
  const hitTestScale = new THREE.Vector3();

  const isSupported = () => ({ platform: AR_PLATFORM.WEBXR, support, reason });

  // Create a render loop that allows us to draw on the AR view.
  const onXRFrame = (time, frame) => {
    session.requestAnimationFrame(onXRFrame);
    gl.bindFramebuffer(
      gl.FRAMEBUFFER,
      session.renderState.baseLayer.framebuffer
    );
    const pose = frame.getViewerPose(referenceSpace);

    if (hitTestSource && pose && hitTestStatus) {
      console.log("webxr hittest", hitTestSource, hitTestStatus);
      const hitTestResults = frame.getHitTestResults(hitTestSource);
      if (hitTestResults.length > 0) {
        console.log("webxr hittest length > 0");
        const result = hitTestResults[0].getPose(referenceSpace);
        hitMatrix.fromArray(result.transform.matrix);
        hitMatrix.decompose(hitTestPosition, hitTestRotation, hitTestScale);
        hitTestCallback({
          position: hitTestPosition,
          rotation: hitTestRotation,
        });
        if (!seekStartEvent) {
          Event.sendSMEvent(AR_POSITION_SEEK.SEEKING);
          seekStartEvent = true;
          console.log("webxr hittest event sent");
        }
      }
    }

    if (pose) {
      // In mobile AR, we only have one view.
      const view = pose.views[0];
      const viewport = session.renderState.baseLayer.getViewport(view);
      renderer.setSize(viewport.width, viewport.height);
      camera.matrix.fromArray(view.transform.matrix);
      camera.projectionMatrix.fromArray(view.projectionMatrix);
      camera.updateMatrixWorld(true);
      renderer.render(scene, camera);
    }
    if (stats) stats.update();
  };

  const createScene = async () => {
    const canvas = document.getElementById("SCENEAR");
    scene = new THREE.Scene();

    renderer = new THREE.WebGLRenderer({
      alpha: true,
      preserveDrawingBuffer: true,
      canvas,
      context: gl,
    });
    renderer.autoClear = false;
    stats = Stats();
    stats.dom.id = THREEJS_NAMES.STATS;
    // document.body.appendChild(stats.dom);
    // The API directly updates the camera matrices.
    // Disable matrix auto updates so three.js doesn't attempt
    // to handle the matrices independently.
    camera = new THREE.PerspectiveCamera();
    camera.matrixAutoUpdate = false;
  };

  const startAR = (setUpScene) => {
    console.log("StateMachine start AR");
    const onSessionStart = async (newSession) => {
      session = newSession;
      userActivationReqd = false;
      gl = createWebGLContext({
        xrCompatible: true,
      });
      createScene();
      // setHdri(hdriTexture);
      console.log("WebXRController session started");
      const onSessionEnd = (/* event */) => {
        newSession.removeEventListener("end", onSessionEnd);
        session = null;
      };
      session.addEventListener("end", onSessionEnd);
      // eslint-disable-next-line no-undef
      const baseLayer = new XRWebGLLayer(session, gl);
      session.updateRenderState({
        baseLayer,
      });

      session.requestReferenceSpace("viewer").then((refSpace) => {
        session.requestHitTestSource({ space: refSpace }).then((source) => {
          hitTestSource = source;
        });
      });

      session.requestReferenceSpace("local").then((refSpace) => {
        referenceSpace = refSpace;
        session.requestAnimationFrame(onXRFrame);
        Event.sendSMEvent(AR_EVENTS.INITIALIZED);
      });
      setUpScene({ scene, renderer, camera });
    };

    console.log("WebXR checking session");
    if (session != null) return;
    // createScene().then(() => {
    console.log("WebXR requestingSession");
    navigator.xr
      .requestSession("immersive-ar", {
        requiredFeatures: ["hit-test", "local"],
        optionalFeatures: ["dom-overlay"],
        domOverlay: { root: document.getElementById("root") },
      })
      .then(onSessionStart)
      .catch((error) => {
        console.log("WebXr Exception", error, JSON.stringify(error));
        if (error.stack.includes("requires user activation")) {
          // renderer.dispose();
          // scene = null;
          // renderer = null;
          userActivationReqd = true;
          Event.sendSMEvent(AR_EVENTS.WEBXR_EXCEPTION);
        }
      });
    // });
  };

  const startHitTest = (callback) => {
    console.log("Starting Hit Test");
    hitTestCallback = callback;
    hitTestStatus = true;
    seekStartEvent = false;
  };

  const stopHitTest = () => {
    hitTestCallback = () => {};
    hitTestStatus = false;
    if (hitTestSource != null) {
      hitTestSource.cancel();
      hitTestSource = null;
    }
  };

  const stopAR = () => {
    stopHitTest();
    if (session != null) session.end();
    session = null;
    const element = document.getElementById("SCENEAR");
    if (element != null) {
      // element.remove();
      element.style.zIndex = 10;
      // element.style.visibility = "hidden";
    }
    [scene, camera, renderer, gl] = [null, null, null, null];
  };

  return {
    isSupported,
    checkSupport: (callback) => {
      if ("xr" in navigator) {
        navigator.xr.isSessionSupported("immersive-ar").then((supported) => {
          if (supported) {
            support = AR_SUPPORT.SUPPORTED;
            reason = null;
          } else {
            support = AR_SUPPORT.NOT_SUPPORTED;
            reason = "AR Not Supported";
          }
          callback(isSupported());
        });
      } else if (window.isSecureContext === false) {
        support = AR_SUPPORT.NOT_SUPPORTED;
        reason = "WEBXR Needs HTTPS";
        callback(isSupported());
      } else {
        support = AR_SUPPORT.NOT_SUPPORTED;
        reason = "WEBXR Not Available";
        callback(isSupported());
      }
    },
    getSceneDetails: () => ({ scene, camera, renderer }),
    startAR: (hdriTexture) => {
      startAR(hdriTexture);
    },
    stopAR,
    startHitTest,
    stopHitTest,
    getUserActivationReqd: () => userActivationReqd,
    getScale: () => 1, // 0.22,
  };
};

const WebXR = mod();
export default WebXR;
